// Single video detail page

import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { getVideoById, getVideoUrl } from '@/lib/supabase'
import type { Video } from '@/types'

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export default function VideoPage() {
  const { id } = useParams<{ id: string }>()
  const [video, setVideo] = useState<Video | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchVideo() {
      if (!id) {
        setError('No video ID provided')
        setLoading(false)
        return
      }

      try {
        const data = await getVideoById(id)
        if (!data) {
          setError('Video not found')
        } else {
          setVideo(data)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load video')
      } finally {
        setLoading(false)
      }
    }

    fetchVideo()
  }, [id])

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-gray-600 border-t-white rounded-full animate-spin" />
      </div>
    )
  }

  if (error || !video) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">
            {error || 'Video not found'}
          </h3>
          <Link
            to="/feed"
            className="inline-block px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
          >
            Back to Feed
          </Link>
        </div>
      </div>
    )
  }

  const videoUrl = getVideoUrl(video.r2_key)

  return (
    <div className="flex-1 flex flex-col">
      {/* Back button */}
      <div className="p-4 border-b border-gray-800">
        <Link
          to="/feed"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to Feed
        </Link>
      </div>

      {/* Video Player */}
      <div className="aspect-video bg-black">
        {videoUrl ? (
          <video
            src={videoUrl}
            className="w-full h-full"
            controls
            autoPlay
            playsInline
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <p className="text-gray-500">Video unavailable</p>
          </div>
        )}
      </div>

      {/* Video Info */}
      <div className="p-4 border-b border-gray-800">
        {/* Location & Time */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400 mb-4">
          {video.neighborhood && (
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
              </svg>
              {video.neighborhood}
            </span>
          )}
          <span className="flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {formatDate(video.created_at)}
          </span>
        </div>

        {/* Tags */}
        {video.tags && video.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {video.tags.map((tag) => (
              <span
                key={tag}
                className="px-2 py-1 bg-gray-800 rounded text-xs text-gray-300"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center gap-6">
          <button className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
            </svg>
            <span>{video.upvotes} upvotes</span>
          </button>

          <span className="flex items-center gap-2 text-gray-400">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
                clipRule="evenodd"
              />
            </svg>
            <span>{video.comment_count} comments</span>
          </span>
        </div>
      </div>

      {/* Archive Info */}
      {(video.ipfs_cid || video.backup_url) && (
        <div className="p-4 border-b border-gray-800">
          <h3 className="text-sm font-medium text-gray-400 mb-2">Archived</h3>
          <div className="flex flex-col gap-1 text-sm">
            {video.ipfs_cid && (
              <a
                href={`https://cloudflare-ipfs.com/ipfs/${video.ipfs_cid}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:underline truncate"
              >
                IPFS: {video.ipfs_cid}
              </a>
            )}
          </div>
        </div>
      )}

      {/* Comments placeholder */}
      <div className="p-4">
        <h3 className="text-lg font-semibold text-white mb-4">
          Comments ({video.comment_count})
        </h3>
        <p className="text-gray-500 text-sm">
          Comments coming soon...
        </p>
      </div>
    </div>
  )
}
